import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { PiWarningCircleLight, PiArrowLeftLight, PiCheckCircleLight, PiCaretRightLight, PiClipboardTextLight, PiCopyLight, PiPencilSimpleLight, PiFileTextLight, PiGaugeLight, PiGlobeLight, PiCircleNotchLight, PiMapPinLight, PiPauseCircleLight, PiPlayCircleLight, PiFloppyDiskLight, PiMagnifyingGlassLight, PiGearLight, PiTargetLight, PiUsersLight, PiXLight } from 'react-icons/pi';
import useJobStore from '../../store/jobStore';
import useJobProcessingStore from '../../store/jobProcessingStore';
import { jobAPI } from '../../services/api';
import JobProfileQuestionStepper, {
  jobQuestionInitialValue,
  toJobProfileAnswerValue
} from '../../components/jobs/JobProfileQuestionStepper';
import SkeletonBlock from '../../components/common/SkeletonBlock';
import { SALARY_PERIODS, USD_CURRENCY, formatCompensationRange } from '../../utils/compensation';

const inputClass = 'w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-sky-400 focus:ring-2 focus:ring-sky-100';
const labelClass = 'mb-2 block text-xs font-medium uppercase tracking-[0.12em] text-slate-500';

const jobTypes = ['full_time', 'part_time', 'contract', 'freelance', 'internship'];
const experienceLevels = ['entry', 'junior', 'mid', 'senior', 'lead', 'executive'];
const labelize = (value) => (
  String(value || 'Not set').replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase())
);

const formatDate = (value) => {
  if (!value) return 'Not set';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not set';
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
};

const formatMoney = (job) => {
  return formatCompensationRange(job?.payRange, 'Not set');
};

const formatParserConfidence = (value) => {
  if (value === null || value === undefined || value === '') return 'Not reported';
  if (typeof value === 'string' && value.trim().endsWith('%')) return value.trim();

  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return String(value);

  const percentage = numeric <= 1 ? numeric * 100 : numeric;
  return `${Math.round(percentage)}%`;
};

const getJobList = (response) => {
  if (Array.isArray(response?.jobs)) return response.jobs;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response)) return response;
  return [];
};

const getJobOptionId = (job) => job?.id || job?.jobId || job?.job_id;

const getJobOptionTitle = (job) => job?.title || job?.jobTitle || job?.job_title || 'Untitled job';

const answeredJobQuestions = (questions = []) => questions.filter((question) => question.answer && !question.answer.skipped);

const statusStyles = {
  active: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  draft: 'bg-slate-50 text-slate-600 border-slate-200',
  paused: 'bg-amber-50 text-amber-700 border-amber-100',
  filled: 'bg-sky-50 text-sky-700 border-sky-100',
  expired: 'bg-rose-50 text-rose-700 border-rose-100'
};

const BACKGROUND_PROCESSING_STAGES = new Set([
  'core_sections_ready',
  'applying_sections',
  'building_matching_profile',
  'matching_profile_queued'
]);
const isProcessingBlocking = (processing) => (
  ['pending', 'processing'].includes(processing?.status) && !BACKGROUND_PROCESSING_STAGES.has(processing?.stage)
);

const Metric = ({ label, value, detail, icon: Icon, onClick, highlight }) => (
  <div
    onClick={onClick}
    className={`rounded-lg border bg-white p-4 transition-all ${onClick ? 'cursor-pointer hover:border-sky-300 hover:shadow-md' : 'shadow-[0_12px_30px_rgba(15,23,42,0.035)]'} ${highlight ? 'border-sky-300 bg-sky-50/30 ring-1 ring-sky-200' : 'border-slate-200'}`}
  >
    <div className={`mb-3 flex h-9 w-9 items-center justify-center rounded-lg ${highlight ? 'bg-sky-100 text-sky-800' : 'bg-[#eef9ff] text-sky-700'}`}>
      <Icon size={14} />
    </div>
    <div className="font-display text-2xl font-normal leading-none text-[#0b1f3a]">{value}</div>
    <div className="mt-2 text-sm font-medium text-slate-700">{label}</div>
    <div className="mt-1 text-xs leading-5 text-slate-500">{detail}</div>
  </div>
);

const Panel = ({ title, icon: Icon, children, action }) => (
  <section className="rounded-lg border border-slate-200 bg-white shadow-[0_12px_30px_rgba(15,23,42,0.035)]">
    <div className="flex items-start justify-between gap-4 border-b border-slate-100 p-4">
      <div className="flex min-w-0 items-start gap-3">
        {Icon && <span className="mt-0.5 flex shrink-0 items-center justify-center text-slate-700"><Icon size={19} /></span>}
        <h2 className="font-display text-[17px] font-normal leading-tight text-[#0b1f3a]">{title}</h2>
      </div>
      {action}
    </div>
    <div className="p-4">{children}</div>
  </section>
);

const JobQuestionCompletionBanner = ({
  progress,
  isHighlighting,
  onContinue,
  onSkip
}) => {
  const completion = Math.max(0, Math.min(100, Number(progress?.completionPercentage) || 0));
  const answered = Number(progress?.answeredQuestions) || 0;
  const total = Number(progress?.totalQuestions) || 0;
  const remaining = Math.max(total - answered, 0);

  if (remaining === 0) return null;

  return (
    <div className="flex flex-col gap-3 rounded-lg bg-[#0b1f3a] px-4 py-3 shadow-md sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-3">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/10 text-sky-400">
          <PiClipboardTextLight size={13} />
        </span>
        <div>
          <div className="text-sm font-semibold text-white">Matching Profile Incomplete</div>
          <div className="text-xs text-slate-300">You have {remaining} unanswered matching questions. Complete them to improve candidate matches.</div>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-4">
        <div className="hidden items-center gap-2 lg:flex">
          <div className="h-1.5 w-24 overflow-hidden rounded-full bg-slate-700">
            <div className="h-full bg-sky-400" style={{ width: `${completion}%` }} />
          </div>
          <span className="text-xs font-medium text-slate-300">{completion}%</span>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={onSkip} className="text-xs font-medium text-slate-400 transition hover:text-white">
            Skip for now
          </button>
          <button onClick={onContinue} className={`whitespace-nowrap rounded-md px-4 py-1.5 text-xs font-medium text-white shadow-sm transition-all duration-1000 ${
            isHighlighting ? 'bg-black btn-premium-base btn-premium-highlight ring-2 ring-sky-400 ring-offset-2 ring-offset-[#0b1f3a]' : 'bg-sky-500 hover:bg-sky-400'
          }`}>
            Complete Job Profile
          </button>
        </div>
      </div>
    </div>
  );
};

const JobDetailsSkeleton = () => (
  <div className="min-h-screen bg-white p-4 sm:p-6 lg:p-8">
    <div className="mx-auto max-w-7xl space-y-6">
      <div>
        <div className="mb-4 flex items-center gap-2">
          <SkeletonBlock className="h-4 w-14 rounded-md" />
          <SkeletonBlock className="h-4 w-4 rounded-md" />
          <SkeletonBlock className="h-4 w-24 rounded-md" />
        </div>

        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0 flex-1">
            <div className="mb-3 flex flex-wrap gap-2">
              <SkeletonBlock className="h-7 w-20 rounded-md" />
              <SkeletonBlock className="h-7 w-28 rounded-md" />
            </div>
            <SkeletonBlock className="h-9 w-[34rem] max-w-full rounded-md" />
            <div className="mt-3 flex flex-wrap gap-3">
              <SkeletonBlock className="h-4 w-36 rounded-md" />
              <SkeletonBlock className="h-4 w-24 rounded-md" />
              <SkeletonBlock className="h-4 w-24 rounded-md" />
              <SkeletonBlock className="h-4 w-32 rounded-md" />
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <SkeletonBlock className="h-10 w-28 rounded-lg" />
            <SkeletonBlock className="h-10 w-24 rounded-lg" />
            <SkeletonBlock className="h-10 w-24 rounded-lg" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="rounded-lg border border-slate-200 bg-white p-4 shadow-[0_12px_30px_rgba(15,23,42,0.035)]">
            <SkeletonBlock className="mb-3 h-9 w-9 rounded-lg" />
            <SkeletonBlock className="h-8 w-20 rounded-md" />
            <SkeletonBlock className="mt-3 h-4 w-28 rounded-md" />
            <SkeletonBlock className="mt-2 h-3 w-36 rounded-md" />
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="space-y-6 xl:col-span-2">
          {[
            { lines: [95, 88, 92, 72], height: 'h-64' },
            { lines: [90, 84, 76], height: 'h-52' },
            { lines: [48, 64, 56, 42], height: 'h-48' }
          ].map((panel, index) => (
            <section key={index} className="rounded-lg border border-slate-200 bg-white shadow-[0_12px_30px_rgba(15,23,42,0.035)]">
              <div className="flex items-start gap-3 border-b border-slate-100 p-4">
                <SkeletonBlock className="h-8 w-8 rounded-lg" />
                <SkeletonBlock className="h-5 w-40 rounded-md" />
              </div>
              <div className={`space-y-3 p-4 ${panel.height}`}>
                {panel.lines.map((width, lineIndex) => (
                  <SkeletonBlock key={lineIndex} className="h-4 rounded-md" style={{ width: `${width}%` }} />
                ))}
              </div>
            </section>
          ))}
        </div>

        <div className="space-y-6">
          <section className="rounded-lg border border-slate-200 bg-white shadow-[0_12px_30px_rgba(15,23,42,0.035)]">
            <div className="flex items-start gap-3 border-b border-slate-100 p-4">
              <SkeletonBlock className="h-8 w-8 rounded-lg" />
              <SkeletonBlock className="h-5 w-28 rounded-md" />
            </div>
            <div className="space-y-4 p-4">
              {Array.from({ length: 5 }).map((_, index) => (
                <div key={index}>
                  <SkeletonBlock className="h-3 w-24 rounded-md" />
                  <SkeletonBlock className="mt-2 h-4 w-36 rounded-md" />
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-lg border border-slate-200 bg-white shadow-[0_12px_30px_rgba(15,23,42,0.035)]">
            <div className="flex items-start gap-3 border-b border-slate-100 p-4">
              <SkeletonBlock className="h-8 w-8 rounded-lg" />
              <SkeletonBlock className="h-5 w-36 rounded-md" />
            </div>
            <div className="space-y-3 p-4">
              {Array.from({ length: 7 }).map((_, index) => (
                <div key={index} className="flex items-center justify-between gap-4">
                  <SkeletonBlock className="h-4 w-36 rounded-md" />
                  <SkeletonBlock className="h-7 w-16 rounded-md" />
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>

      <section className="rounded-lg border border-slate-200 bg-white shadow-[0_12px_30px_rgba(15,23,42,0.035)]">
        <div className="border-b border-slate-100 p-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-start gap-3">
              <SkeletonBlock className="h-8 w-8 rounded-lg" />
              <div>
                <SkeletonBlock className="h-5 w-48 rounded-md" />
                <SkeletonBlock className="mt-2 h-3 w-80 max-w-full rounded-md" />
              </div>
            </div>
            <SkeletonBlock className="h-9 w-28 rounded-lg" />
          </div>
        </div>
        <div className="space-y-5 p-4">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0 flex-1">
                <SkeletonBlock className="h-4 w-44 rounded-md" />
                <SkeletonBlock className="mt-3 h-2 w-full rounded-full" />
              </div>
              <SkeletonBlock className="h-10 w-28 rounded-lg" />
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {Array.from({ length: 12 }).map((_, index) => (
                <SkeletonBlock key={index} className="h-9 w-9 rounded-lg" />
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 sm:p-5">
            <SkeletonBlock className="h-3 w-32 rounded-md" />
            <SkeletonBlock className="mt-3 h-7 w-[34rem] max-w-full rounded-md" />
            <SkeletonBlock className="mt-3 h-4 w-[42rem] max-w-full rounded-md" />
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              {Array.from({ length: 4 }).map((_, index) => (
                <SkeletonBlock key={index} className="h-11 rounded-lg" />
              ))}
            </div>
            <div className="mt-6 flex flex-col gap-3 border-t border-slate-200 pt-4 sm:flex-row sm:justify-between">
              <SkeletonBlock className="h-4 w-28 rounded-md" />
              <div className="flex gap-2">
                <SkeletonBlock className="h-10 w-24 rounded-lg" />
                <SkeletonBlock className="h-10 w-36 rounded-lg" />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
);

const JobDetailsPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const {
    currentJob,
    jobRequirements,
    jobStats,
    aiParsedData,
    loading,
    fetchJobById,
    updateJob,
    publishJob,
    unpublishJob,
    fetchJobProfileSetup,
    saveJobProfileAnswers
  } = useJobStore();
  const retryProcessing = useJobProcessingStore(state => state.retry);

  const [isEditing, setIsEditing] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [profileSetup, setProfileSetup] = useState(null);
  const [answers, setAnswers] = useState({});
  const [skippedAnswers, setSkippedAnswers] = useState({});
  const [savingProfileQuestionKey, setSavingProfileQuestionKey] = useState('');
  const [dirtyProfileQuestionKeys, setDirtyProfileQuestionKeys] = useState({});
  const [recentlySavedProfileQuestionKeys, setRecentlySavedProfileQuestionKeys] = useState({});
  const [activeProfileQuestionIndex, setActiveProfileQuestionIndex] = useState(0);
  const [profileQuestionsFinished, setProfileQuestionsFinished] = useState(false);
  const [answerSourceJobs, setAnswerSourceJobs] = useState([]);
  const [answerSourceJobId, setAnswerSourceJobId] = useState('');
  const [answerSourceJobsLoading, setAnswerSourceJobsLoading] = useState(false);
  const [importingAnswers, setImportingAnswers] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [hasSkippedProfile, setHasSkippedProfile] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    requirementsText: '',
    jobType: 'full_time',
    experienceLevel: 'mid',
    location: '',
    payRangeMin: '',
    payRangeMax: '',
    payCurrency: 'USD',
    payPeriod: '',
    isRemoteAllowed: false,
    positionsAvailable: 1,
    contractDurationMonths: '',
    targetStartDate: '',
    matchBandPreference: 'A',
    nonNegotiableFilters: []
  });

  const hydrateForm = useCallback((job) => {
    if (!job) return;
    setFormData({
      title: job.title || '',
      description: job.description || '',
      requirementsText: job.requirementsText || '',
      jobType: job.jobType || 'full_time',
      experienceLevel: job.experienceLevel || 'mid',
      location: job.location || '',
      payRangeMin: job.payRange?.min || '',
      payRangeMax: job.payRange?.max || '',
      payCurrency: USD_CURRENCY,
      payPeriod: job.payRange?.period || '',
      isRemoteAllowed: Boolean(job.isRemoteAllowed),
      positionsAvailable: job.positionsAvailable || 1,
      contractDurationMonths: job.contractDurationMonths || '',
      targetStartDate: job.targetStartDate ? String(job.targetStartDate).split('T')[0] : '',
      matchBandPreference: job.matchBandPreference || 'A',
      nonNegotiableFilters: job.nonNegotiableFilters || []
    });
  }, []);

  const jobQuestionProgress = profileSetup?.questionProgress || {};
  const totalJobQuestions = Number(jobQuestionProgress.totalQuestions || profileSetup?.questions?.length || 0);
  const answeredJobQuestionCount = Number(jobQuestionProgress.answeredQuestions || 0);
  const shouldShowJobQuestionsBanner = totalJobQuestions > 0 && answeredJobQuestionCount < totalJobQuestions;
  const processingActive = isProcessingBlocking(currentJob?.processing);

  const primaryAction = (currentJob?.status !== 'active' && shouldShowJobQuestionsBanner && !hasSkippedProfile)
    ? 'complete_profile'
    : (currentJob?.status !== 'active')
      ? 'publish_job'
      : 'review_matches';

  const hydrateAnswers = useCallback((setup) => {
    const nextAnswers = {};
    const nextSkipped = {};
    const questions = setup?.questions || [];
    questions.forEach((question) => {
      if (question.answer) {
        nextAnswers[question.questionKey] = jobQuestionInitialValue(question);
        nextSkipped[question.questionKey] = Boolean(question.answer.skipped);
      }
    });
    setAnswers(nextAnswers);
    setSkippedAnswers(nextSkipped);
    setDirtyProfileQuestionKeys({});
    setRecentlySavedProfileQuestionKeys({});

    const firstUnansweredIndex = questions.findIndex((question) => !question.answer || question.answer.skipped);
    const allQuestionsAnswered = questions.length > 0 && firstUnansweredIndex === -1;
    setProfileQuestionsFinished(allQuestionsAnswered);
    setActiveProfileQuestionIndex(allQuestionsAnswered ? 0 : Math.max(firstUnansweredIndex, 0));
  }, []);

  const load = useCallback(async () => {
    if (!jobId) return;
    setPageLoading(true);
    const [jobResult, setupResult] = await Promise.all([
      fetchJobById(jobId),
      fetchJobProfileSetup(jobId)
    ]);

    if (!jobResult.success) {
      toast.error(jobResult.error);
      navigate('/jobs');
      return;
    }

    hydrateForm(jobResult.job);
    if (setupResult.success) {
      setProfileSetup(setupResult.setup);
      hydrateAnswers(setupResult.setup);
    }
    setPageLoading(false);
  }, [fetchJobById, fetchJobProfileSetup, hydrateAnswers, hydrateForm, jobId, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (pageLoading || !jobId) return undefined;

    let cancelled = false;
    const loadAnswerSources = async () => {
      setAnswerSourceJobsLoading(true);
      try {
        const response = await jobAPI.getEmployerJobs({
          limit: 75,
          offset: 0,
          sortBy: 'created_at',
          sortOrder: 'DESC'
        });
        if (cancelled) return;

        const jobs = getJobList(response)
          .filter((job) => String(getJobOptionId(job)) !== String(jobId))
          .filter((job) => getJobOptionId(job));

        setAnswerSourceJobs(jobs);
        setAnswerSourceJobId((current) => jobs.some((job) => String(getJobOptionId(job)) === String(current)) ? current : '');
      } catch (error) {
        if (!cancelled) {
          setAnswerSourceJobs([]);
        }
      } finally {
        if (!cancelled) setAnswerSourceJobsLoading(false);
      }
    };

    loadAnswerSources();
    return () => {
      cancelled = true;
    };
  }, [jobId, pageLoading]);

  useEffect(() => {
    if (!isEditing) hydrateForm(currentJob);
  }, [currentJob, hydrateForm, isEditing]);


  const updateForm = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    updateForm(name, type === 'checkbox' ? checked : value);
  };

  const validate = () => {
    const nextErrors = {};
    if (formData.title.trim().length < 3) nextErrors.title = 'Job title must be at least 3 characters.';
    if (formData.description.trim().length < 50) nextErrors.description = 'Description must be at least 50 characters.';
    if (formData.requirementsText.trim().length < 20) nextErrors.requirementsText = 'Requirements must be at least 20 characters.';
    if (formData.location.trim().length < 2) nextErrors.location = 'Location is required.';
    if ((formData.payRangeMin && !formData.payRangeMax) || (!formData.payRangeMin && formData.payRangeMax)) {
      nextErrors.payRangeMax = 'Provide both minimum and maximum compensation, or leave both blank.';
    } else if (formData.payRangeMin && formData.payRangeMax && Number(formData.payRangeMin) >= Number(formData.payRangeMax)) {
      nextErrors.payRangeMax = 'Maximum compensation must be greater than minimum.';
    }
    if ((formData.payRangeMin || formData.payRangeMax) && !formData.payPeriod) {
      nextErrors.payPeriod = 'Select the period for this compensation range.';
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const buildPayload = () => ({
    title: formData.title.trim(),
    description: formData.description.trim(),
    requirementsText: formData.requirementsText.trim(),
    jobType: formData.jobType,
    experienceLevel: formData.experienceLevel,
    location: formData.location.trim(),
    isRemoteAllowed: formData.isRemoteAllowed,
    payRangeMin: formData.payRangeMin ? Number(formData.payRangeMin) : null,
    payRangeMax: formData.payRangeMax ? Number(formData.payRangeMax) : null,
    payCurrency: USD_CURRENCY,
    payPeriod: formData.payRangeMin && formData.payRangeMax ? formData.payPeriod : null,
    positionsAvailable: Number(formData.positionsAvailable) || 1,
    contractDurationMonths: formData.contractDurationMonths ? Number(formData.contractDurationMonths) : null,
    targetStartDate: formData.targetStartDate || null,
    matchBandPreference: formData.matchBandPreference,
    nonNegotiableFilters: formData.nonNegotiableFilters
  });

  const handleSave = async () => {
    if (!validate()) return;
    const result = await updateJob(jobId, buildPayload());
    if (result.success) {
      toast.success('Job updated.');
      setIsEditing(false);
      load();
    } else {
      toast.error(result.error);
    }
  };

  const handlePublishToggle = async () => {
    if (processingActive) {
      toast.error('AI drafting is still in progress. You can keep editing while it finishes.');
      return;
    }
    const result = currentJob?.status === 'active' ? await unpublishJob(jobId) : await publishJob(jobId);
    if (result.success) {
      toast.success(currentJob?.status === 'active' ? 'Job paused.' : 'Job published. Matching will finish in the background.');
      load();
    } else {
      toast.error(result.error);
    }
  };

  const handleRetryProcessing = async () => {
    const result = await retryProcessing(currentJob?.processing?.runId);
    if (result.success) toast.success('Job processing retry queued.');
    else toast.error(result.error);
  };

  const addFilter = (value) => {
    const cleaned = value.trim();
    if (!cleaned || formData.nonNegotiableFilters.includes(cleaned)) return;
    updateForm('nonNegotiableFilters', [...formData.nonNegotiableFilters, cleaned]);
  };

  const removeFilter = (filter) => {
    updateForm('nonNegotiableFilters', formData.nonNegotiableFilters.filter(item => item !== filter));
  };

  const buildProfilePayloadFor = (question, { requireAnswer = true } = {}) => {
    const nextErrors = {};
    if (!question) return { payload: [], nextErrors };

    const skipped = Boolean(skippedAnswers[question.questionKey]);
    const rawValue = answers[question.questionKey];
    const value = toJobProfileAnswerValue(question, rawValue);

    if (question.answerType === 'object_list' && !skipped && typeof rawValue === 'string' && rawValue && value === null) {
      nextErrors[question.questionKey] = 'Use the structured fields or remove the invalid entry.';
      return { payload: [], nextErrors };
    }

    if (!skipped && value === null) {
      if (requireAnswer) nextErrors[question.questionKey] = 'Answer this question or choose Skip.';
      return { payload: [], nextErrors };
    }

    return {
      payload: [{ questionKey: question.questionKey, answerValue: skipped ? null : value, skipped }],
      nextErrors
    };
  };

  const handleProfileAnswerChange = (questionKey, value) => {
    setProfileQuestionsFinished(false);
    setAnswers(prev => ({ ...prev, [questionKey]: value }));
    setDirtyProfileQuestionKeys(prev => ({ ...prev, [questionKey]: true }));
    setRecentlySavedProfileQuestionKeys(prev => {
      if (!prev[questionKey]) return prev;
      const next = { ...prev };
      delete next[questionKey];
      return next;
    });
    if (errors[questionKey]) setErrors(prev => ({ ...prev, [questionKey]: '' }));
  };

  const handleProfileSkipChange = (questionKey, checked) => {
    setProfileQuestionsFinished(false);
    setSkippedAnswers(prev => ({ ...prev, [questionKey]: checked }));
    setDirtyProfileQuestionKeys(prev => ({ ...prev, [questionKey]: true }));
    if (errors[questionKey]) setErrors(prev => ({ ...prev, [questionKey]: '' }));
  };

  const applySavedProfileQuestion = (question, result) => {
    const savedQuestion = result?.setup?.questions?.find(item => item.questionKey === question.questionKey);
    const savedAnswer = savedQuestion?.answer || result?.response?.answers?.find?.(answer => answer.questionKey === question.questionKey);

    if (result?.setup) setProfileSetup(result.setup);

    if (savedQuestion?.answer) {
      setAnswers(prev => ({ ...prev, [question.questionKey]: jobQuestionInitialValue(savedQuestion) }));
      setSkippedAnswers(prev => ({ ...prev, [question.questionKey]: Boolean(savedQuestion.answer.skipped) }));
    } else if (savedAnswer) {
      setAnswers(prev => ({ ...prev, [question.questionKey]: savedAnswer.answerValue }));
      setSkippedAnswers(prev => ({ ...prev, [question.questionKey]: Boolean(savedAnswer.skipped) }));
    }

    setErrors(prev => {
      const next = { ...prev };
      delete next[question.questionKey];
      return next;
    });
    setDirtyProfileQuestionKeys(prev => {
      const next = { ...prev };
      delete next[question.questionKey];
      return next;
    });
    setRecentlySavedProfileQuestionKeys(prev => ({ ...prev, [question.questionKey]: true }));
  };

  const saveProfileQuestion = async (question, { advance = false, finish = false } = {}) => {
    const { payload, nextErrors } = buildProfilePayloadFor(question);
    if (Object.keys(nextErrors).length) {
      setErrors(prev => ({ ...prev, ...nextErrors }));
      return false;
    }

    setSavingProfileQuestionKey(question.questionKey);
    const result = await saveJobProfileAnswers(jobId, payload);
    setSavingProfileQuestionKey('');

    if (!result.success) {
      toast.error(result.error);
      return false;
    }

    applySavedProfileQuestion(question, result);
    if (advance) {
      const questionIndex = (profileSetup?.questions || []).findIndex(item => item.questionKey === question.questionKey);
      setActiveProfileQuestionIndex(Math.min(Math.max(questionIndex, 0) + 1, (profileSetup?.questions || []).length - 1));
    }
    if (finish) setProfileQuestionsFinished(true);
    return true;
  };

  const scrollToJobProfileQuestions = () => {
    setActiveTab('matching');
    window.setTimeout(() => {
      document.getElementById('job-matching-profile-questions')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 60);
  };


  const importAnswersFromSourceJob = async () => {
    if (!answerSourceJobId) return;

    setImportingAnswers(true);
    try {
      const sourceSetup = await jobAPI.getProfileSetup(answerSourceJobId);
      const sourceQuestions = sourceSetup?.questions || [];
      const payload = answeredJobQuestions(sourceQuestions)
        .map((question) => ({
          questionKey: question.questionKey,
          answerValue: question.answer?.value ?? question.answer?.answerValue ?? null,
          skipped: false
        }))
        .filter((answer) => answer.questionKey && answer.answerValue !== null && answer.answerValue !== undefined && answer.answerValue !== '');

      if (!payload.length) {
        toast.error('The selected job has no saved matching answers to import.');
        return;
      }

      const result = await saveJobProfileAnswers(jobId, payload);
      if (!result.success) {
        toast.error(result.error);
        return;
      }

      if (result.setup) {
        setProfileSetup(result.setup);
        hydrateAnswers(result.setup);
      }

      const sourceJob = answerSourceJobs.find((job) => String(getJobOptionId(job)) === String(answerSourceJobId));
      toast.success(`Imported ${payload.length} matching ${payload.length === 1 ? 'answer' : 'answers'} from ${getJobOptionTitle(sourceJob)}.`);
      scrollToJobProfileQuestions();
    } catch (error) {
      toast.error(error?.message || 'Could not import answers from that job.');
    } finally {
      setImportingAnswers(false);
    }
  };

  if (pageLoading) {
    return <JobDetailsSkeleton />;
  }

  if (!currentJob) return null;

  return (
    <div className="min-h-screen bg-white p-4 sm:p-6 lg:p-8">
      <style>{`
        @keyframes shimmer-sweep {
          0% { background-position: 200% center; }
          40% { background-position: -200% center; }
          100% { background-position: -200% center; }
        }
        @keyframes subtle-pulse-ring {
          0% { box-shadow: 0 0 0 0 rgba(0, 0, 0, 0.4); }
          20% { box-shadow: 0 0 0 4px rgba(0, 0, 0, 0); }
          100% { box-shadow: 0 0 0 0 rgba(0, 0, 0, 0); }
        }
        .btn-premium-base {
          position: relative;
          overflow: hidden;
        }
        .btn-premium-base::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(110deg, transparent 25%, rgba(255, 255, 255, 0.4) 50%, transparent 75%);
          background-size: 200% 100%;
          animation: shimmer-sweep 5s infinite linear;
          opacity: 0;
          transition: opacity 1s ease-in-out;
          pointer-events: none;
        }
        .btn-premium-highlight::after {
          opacity: 1;
        }
        .btn-premium-highlight {
          animation: subtle-pulse-ring 5s infinite cubic-bezier(0.4, 0, 0.2, 1);
        }
      `}</style>
      <div className="mx-auto max-w-7xl space-y-6">
        <div>
          <nav className="mb-4 flex items-center gap-2 text-sm text-slate-500">
            <button onClick={() => navigate('/jobs')} className="inline-flex items-center gap-1 hover:text-[#0b1f3a]">
              <PiArrowLeftLight size={12} /> Jobs
            </button>
            <PiCaretRightLight size={12} />
            <span className="font-medium text-[#0b1f3a]">Job details</span>
          </nav>

          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0 flex-1 pr-4">
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <span className={`rounded-md border px-2.5 py-1 text-xs font-medium capitalize ${statusStyles[currentJob.status] || statusStyles.draft}`}>
                  {labelize(currentJob.status)}
                </span>
                {currentJob.processing && (
                  <span className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium ${
                    processingActive ? 'bg-sky-50 text-sky-700' : currentJob.processing.status === 'failed' ? 'bg-rose-50 text-rose-700' : 'bg-emerald-50 text-emerald-700'
                  }`}>
                    {processingActive && <PiCircleNotchLight className="animate-spin" size={12} />}
                    {processingActive ? `${currentJob.processing.progress}% AI drafting` : currentJob.processing.status === 'failed' ? 'AI needs attention' : 'AI draft ready'}
                  </span>
                )}
                {currentJob.isRemoteAllowed && <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700"><PiGlobeLight size={12} /> Remote allowed</span>}
              </div>
              <h1 className="font-display text-3xl font-normal tracking-tight text-[#0b1f3a]">{currentJob.title}</h1>
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-2 text-sm text-slate-500">
                <span className="inline-flex items-center gap-1"><PiMapPinLight size={12} /> {currentJob.location || 'No location'}</span>
                <span>{labelize(currentJob.jobType)}</span>
                <span>{labelize(currentJob.experienceLevel)}</span>
                <span>Published {formatDate(currentJob.publishedAt)}</span>
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-2">
              <button onClick={() => setIsEditing(prev => !prev)} className="inline-flex h-10 shrink-0 items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-600 transition hover:bg-slate-50">
                {isEditing ? <PiXLight size={13} /> : <PiPencilSimpleLight size={13} />}
                {isEditing ? 'Cancel' : 'Edit'}
              </button>
              <button onClick={handlePublishToggle} disabled={loading || processingActive} className={`inline-flex h-10 shrink-0 items-center gap-2 rounded-lg px-4 text-sm font-medium transition-colors duration-1000 disabled:opacity-60 ${
                currentJob.status !== 'active'
                  ? `text-white shadow-sm ${primaryAction === 'publish_job' ? 'bg-black btn-premium-base btn-premium-highlight' : 'bg-[#0b1f3a] hover:bg-[#12345c]'}`
                  : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
              }`}>
                {currentJob.status === 'active' ? <PiPauseCircleLight size={13} /> : <PiGlobeLight size={13} />}
                {currentJob.status === 'active' ? 'Close Job' : 'Publish Job'}
              </button>
              <button onClick={() => navigate(`/jobs/${jobId}/matches`)} disabled={processingActive} className={`inline-flex h-10 shrink-0 items-center gap-2 rounded-lg px-5 text-sm font-medium transition-colors duration-1000 disabled:cursor-not-allowed disabled:opacity-50 ${
                currentJob.status === 'active'
                  ? `text-white shadow-sm ${primaryAction === 'review_matches' ? 'bg-black btn-premium-base btn-premium-highlight' : 'bg-[#0b1f3a] hover:bg-[#12345c]'}`
                  : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
              }`}>
                <PiUsersLight size={13} /> Review Matches
                {Number(jobStats.totalMatches || currentJob.stats?.totalMatches) > 0 && (
                  <span className={`ml-1 rounded-full px-2 py-0.5 text-xs font-semibold ${currentJob.status === 'active' ? 'bg-white/20' : 'bg-slate-100 text-slate-500'}`}>{jobStats.totalMatches || currentJob.stats?.totalMatches}</span>
                )}
              </button>
            </div>
          </div>
        </div>

        {currentJob.processing && currentJob.processing.status !== 'completed' && (
          <section className={`rounded-xl border p-4 shadow-[0_16px_50px_rgba(15,23,42,0.06)] ${
            currentJob.processing.status === 'failed' ? 'border-rose-200 bg-rose-50' : 'border-sky-200 bg-white'
          }`}>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3">
                <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
                  currentJob.processing.status === 'failed' ? 'bg-rose-100 text-rose-700' : 'bg-[#0b1f3a] text-sky-300'
                }`}>
                  {currentJob.processing.status === 'failed' ? <PiWarningCircleLight size={14} /> : <PiFileTextLight size={14} />}
                </span>
                <div>
                  <div className="text-sm font-medium text-[#0b1f3a]">
                    {currentJob.processing.status === 'failed' ? 'The draft is safe, but AI processing needs attention' : 'AI is drafting this job in the background'}
                  </div>
                  <div className="mt-1 text-xs leading-5 text-slate-500">
                    {currentJob.processing.status === 'failed'
                      ? 'Continue editing manually or retry processing. Employer edits always take priority.'
                      : 'You can complete hiring logistics or leave this page. Extracted fields appear as soon as validated sections are ready.'}
                  </div>
                </div>
              </div>
              {currentJob.processing.status === 'failed' ? (
                <button onClick={handleRetryProcessing} className="inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-rose-700 px-4 text-xs font-medium text-white hover:bg-rose-800">
                  <PiPlayCircleLight size={12} /> Retry AI
                </button>
              ) : (
                <div className="min-w-[190px]">
                  <div className="mb-1.5 flex items-center justify-between text-[11px] font-medium text-slate-500">
                    <span>{labelize(currentJob.processing.stage)}</span>
                    <span>{Number(currentJob.processing.progress || 0)}%</span>
                  </div>
                  <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
                    <div className="h-full rounded-full bg-sky-600 transition-all duration-700" style={{ width: `${Math.max(3, Number(currentJob.processing.progress || 0))}%` }} />
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        {shouldShowJobQuestionsBanner && !hasSkippedProfile && activeTab !== 'matching' && (
          <JobQuestionCompletionBanner
            progress={jobQuestionProgress}
            isHighlighting={primaryAction === 'complete_profile'}
            onContinue={scrollToJobProfileQuestions}
            onSkip={() => setHasSkippedProfile(true)}
          />
        )}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <Metric
            icon={PiUsersLight}
            label="Candidates Matched"
            value={jobStats.totalMatches || currentJob.stats?.totalMatches || 0}
            detail="Click to review candidate profiles"
            onClick={() => navigate(`/jobs/${jobId}/matches`)}
            highlight={true}
          />
          <Metric icon={PiCheckCircleLight} label="Mutual Interest" value={jobStats.mutualMatches || currentJob.stats?.mutualMatches || 0} detail="Candidate accepted your invite" />
          <Metric icon={PiTargetLight} label="Hiring Progress" value={`${currentJob.positionsFilled} / ${currentJob.positionsAvailable}`} detail={`${currentJob.positionsRemaining} positions remaining`} />
          <Metric icon={PiGaugeLight} label="Profile Strength" value={`${Math.round(currentJob.completionPercentage || 0)}%`} detail={currentJob.isComplete ? 'Ready to publish' : 'Add more details to improve'} />
        </div>

        {isEditing ? (
          <Panel
            title="Edit job"
            icon={PiPencilSimpleLight}
            action={
              <button onClick={handleSave} disabled={loading} className="inline-flex h-9 items-center gap-2 rounded-lg bg-[#0b1f3a] px-3 text-sm font-medium text-white transition hover:bg-[#12345c] disabled:opacity-60">
                {loading ? <PiCircleNotchLight className="animate-spin" size={12} /> : <PiFloppyDiskLight size={12} />} Save
              </button>
            }
          >
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <label className={labelClass}>Title</label>
                <input name="title" value={formData.title} onChange={handleInputChange} className={inputClass} />
                {errors.title && <p className="mt-1 text-xs text-rose-600">{errors.title}</p>}
              </div>
              <div>
                <label className={labelClass}>Job type</label>
                <select name="jobType" value={formData.jobType} onChange={handleInputChange} className={inputClass}>
                  {jobTypes.map(type => <option key={type} value={type}>{labelize(type)}</option>)}
                </select>
              </div>
              <div>
                <label className={labelClass}>Experience level</label>
                <select name="experienceLevel" value={formData.experienceLevel} onChange={handleInputChange} className={inputClass}>
                  {experienceLevels.map(level => <option key={level} value={level}>{labelize(level)}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className={labelClass}>Location</label>
                <input name="location" value={formData.location} onChange={handleInputChange} className={inputClass} />
                {errors.location && <p className="mt-1 text-xs text-rose-600">{errors.location}</p>}
                <label className="mt-3 flex items-center gap-2 text-sm font-medium text-slate-700">
                  <input type="checkbox" name="isRemoteAllowed" checked={formData.isRemoteAllowed} onChange={handleInputChange} className="rounded border-slate-300 text-sky-600 focus:ring-sky-500" />
                  Remote work allowed
                </label>
              </div>
              <div className="md:col-span-2">
                <label className={labelClass}>Description</label>
                <textarea name="description" rows={7} value={formData.description} onChange={handleInputChange} className={inputClass} />
                {errors.description && <p className="mt-1 text-xs text-rose-600">{errors.description}</p>}
              </div>
              <div className="md:col-span-2">
                <label className={labelClass}>Requirements</label>
                <textarea name="requirementsText" rows={7} value={formData.requirementsText} onChange={handleInputChange} className={inputClass} />
                {errors.requirementsText && <p className="mt-1 text-xs text-rose-600">{errors.requirementsText}</p>}
              </div>
              <div>
                <label className={labelClass}>Minimum compensation</label>
                <input type="number" name="payRangeMin" value={formData.payRangeMin} onChange={handleInputChange} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Maximum compensation</label>
                <input type="number" name="payRangeMax" value={formData.payRangeMax} onChange={handleInputChange} className={inputClass} />
                {errors.payRangeMax && <p className="mt-1 text-xs text-rose-600">{errors.payRangeMax}</p>}
              </div>
              <div>
                <label className={labelClass}>Currency</label>
                <input value={USD_CURRENCY} readOnly className={`${inputClass} bg-slate-50`} />
              </div>
              <div>
                <label className={labelClass}>Pay period</label>
                <select name="payPeriod" value={formData.payPeriod} onChange={handleInputChange} className={inputClass}>
                  <option value="">Select period</option>
                  {SALARY_PERIODS.map(period => <option key={period.value} value={period.value}>{period.label}</option>)}
                </select>
                {errors.payPeriod && <p className="mt-1 text-xs text-rose-600">{errors.payPeriod}</p>}
              </div>
              <div>
                <label className={labelClass}>Positions available</label>
                <input type="number" min="1" max="1000" name="positionsAvailable" value={formData.positionsAvailable} onChange={handleInputChange} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Contract months</label>
                <input type="number" min="1" max="60" name="contractDurationMonths" value={formData.contractDurationMonths} onChange={handleInputChange} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Target start date</label>
                <input type="date" name="targetStartDate" value={formData.targetStartDate} onChange={handleInputChange} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Preferred match band</label>
                <select name="matchBandPreference" value={formData.matchBandPreference} onChange={handleInputChange} className={inputClass}>
                  <option value="A">Band A</option>
                  <option value="B">Band B</option>
                  <option value="C">Band C</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className={labelClass}>Non-negotiable filters</label>
                <input
                  className={inputClass}
                  placeholder="Type a requirement and press Enter"
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      addFilter(event.currentTarget.value);
                      event.currentTarget.value = '';
                    }
                  }}
                />
                <div className="mt-2 flex flex-wrap gap-2">
                  {formData.nonNegotiableFilters.map(filter => (
                    <span key={filter} className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600">
                      {filter}
                      <button onClick={() => removeFilter(filter)} className="text-slate-400 hover:text-rose-600"><PiXLight size={12} /></button>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </Panel>
        ) : (
          <div className="space-y-6">
            <div className="flex border-b border-slate-200">
              <button
                onClick={() => setActiveTab('overview')}
                className={`relative pb-3 pt-2 px-4 text-sm font-medium transition-colors ${activeTab === 'overview' ? 'text-sky-700' : 'text-slate-500 hover:text-slate-700'}`}
              >
                Overview
                {activeTab === 'overview' && <div className="absolute bottom-[-1px] left-0 right-0 h-0.5 rounded-t-full bg-sky-500" />}
              </button>
              <button
                onClick={() => setActiveTab('matching')}
                className={`relative flex items-center gap-2 pb-3 pt-2 px-4 text-sm font-medium transition-colors ${activeTab === 'matching' ? 'text-sky-700' : 'text-slate-500 hover:text-slate-700'}`}
              >
                Matching Profile
                {shouldShowJobQuestionsBanner && <span className="flex h-2 w-2 rounded-full bg-amber-500" />}
                {activeTab === 'matching' && <div className="absolute bottom-[-1px] left-0 right-0 h-0.5 rounded-t-full bg-sky-500" />}
              </button>
              <button
                onClick={() => setActiveTab('advanced')}
                className={`relative pb-3 pt-2 px-4 text-sm font-medium transition-colors ${activeTab === 'advanced' ? 'text-sky-700' : 'text-slate-500 hover:text-slate-700'}`}
              >
                Advanced
                {activeTab === 'advanced' && <div className="absolute bottom-[-1px] left-0 right-0 h-0.5 rounded-t-full bg-sky-500" />}
              </button>
            </div>

            {activeTab === 'overview' && (
              <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
                <div className="space-y-6 xl:col-span-2">
                  <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
                    <h2 className="mb-6 font-display text-xl font-normal text-[#0b1f3a]">Job Description</h2>
                    <div className="prose prose-sm prose-slate max-w-none text-slate-600">
                      <div className="whitespace-pre-wrap">{currentJob.description || 'No description provided.'}</div>

                      <h3 className="mb-4 mt-8 font-display text-lg font-normal text-[#0b1f3a]">Requirements</h3>
                      <div className="whitespace-pre-wrap">{currentJob.requirementsText || 'No requirements provided.'}</div>

                      {currentJob.nonNegotiableFilters?.length > 0 && (
                        <div className="mt-8 rounded-lg border border-rose-100 bg-rose-50/50 p-4">
                          <h4 className="mb-3 text-xs font-semibold uppercase tracking-[0.12em] text-rose-800">Absolute Non-negotiables</h4>
                          <ul className="m-0 flex list-none flex-wrap gap-2 p-0">
                            {currentJob.nonNegotiableFilters.map(filter => (
                              <li key={filter} className="m-0 rounded-md border border-rose-200 bg-white px-3 py-1.5 text-sm font-medium text-rose-700 shadow-sm">{filter}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </section>
                </div>

                <div className="space-y-6">
                  <Panel title="Job Facts" icon={PiTargetLight}>
                    <dl className="space-y-4 text-sm">
                      <div><dt className="text-xs font-medium uppercase tracking-[0.12em] text-slate-400">Compensation</dt><dd className="mt-1 font-medium text-[#0b1f3a]">{formatMoney(currentJob)}</dd></div>
                      <div><dt className="text-xs font-medium uppercase tracking-[0.12em] text-slate-400">Positions</dt><dd className="mt-1 font-medium text-[#0b1f3a]">{currentJob.positionsFilled} filled, {currentJob.positionsRemaining} remaining</dd></div>
                      <div><dt className="text-xs font-medium uppercase tracking-[0.12em] text-slate-400">Match band</dt><dd className="mt-1 font-medium text-[#0b1f3a]">Band {currentJob.matchBandPreference}</dd></div>
                      <div><dt className="text-xs font-medium uppercase tracking-[0.12em] text-slate-400">Target start</dt><dd className="mt-1 font-medium text-[#0b1f3a]">{formatDate(currentJob.targetStartDate)}</dd></div>
                      <div><dt className="text-xs font-medium uppercase tracking-[0.12em] text-slate-400">Duration</dt><dd className="mt-1 font-medium text-[#0b1f3a]">{currentJob.contractDurationMonths ? `${currentJob.contractDurationMonths} months` : 'Permanent or not set'}</dd></div>
                    </dl>
                  </Panel>
                </div>
              </div>
            )}

            {activeTab === 'matching' && (
              <div className="space-y-6">
                {answerSourceJobs.length > 0 && (
                  <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="mb-3 flex items-center gap-2">
                      <PiCopyLight size={13} className="text-slate-500" />
                      <span className="text-sm font-medium text-[#0b1f3a]">Reuse previous answers</span>
                    </div>
                    <div className="flex flex-col gap-3 sm:flex-row">
                      <select
                        value={answerSourceJobId}
                        onChange={(event) => setAnswerSourceJobId(event.target.value)}
                        disabled={answerSourceJobsLoading || importingAnswers}
                        className="min-w-0 flex-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:bg-white focus:ring-2 focus:ring-sky-100 disabled:cursor-not-allowed disabled:text-slate-400"
                      >
                        <option value="">{answerSourceJobsLoading ? 'Loading previous jobs...' : 'Select a previous job to copy answers from'}</option>
                        {answerSourceJobs.map((job) => (
                          <option key={getJobOptionId(job)} value={getJobOptionId(job)}>
                            {getJobOptionTitle(job)}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={importAnswersFromSourceJob}
                        disabled={!answerSourceJobId || importingAnswers}
                        className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-lg bg-[#0b1f3a] px-4 text-sm font-medium text-white transition hover:bg-[#12345c] disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {importingAnswers ? <PiCircleNotchLight className="animate-spin" size={13} /> : <PiCopyLight size={13} />}
                        Import answers
                      </button>
                    </div>
                    <p className="mt-3 text-xs leading-5 text-slate-500">
                      Instantly populate these questions by copying answered matching-profile fields from another job you've created.
                    </p>
                  </div>
                )}
                <div id="job-matching-profile-questions" className="scroll-mt-6">
                  <JobProfileQuestionStepper
                    profileSetup={profileSetup}
                    answers={answers}
                    skippedAnswers={skippedAnswers}
                    errors={errors}
                    activeIndex={activeProfileQuestionIndex}
                    finished={profileQuestionsFinished}
                    savingQuestionKey={savingProfileQuestionKey}
                    dirtyQuestionKeys={dirtyProfileQuestionKeys}
                    recentlySavedQuestionKeys={recentlySavedProfileQuestionKeys}
                    onAnswerChange={handleProfileAnswerChange}
                    onSkipChange={handleProfileSkipChange}
                    onActiveIndexChange={setActiveProfileQuestionIndex}
                    onFinishedChange={setProfileQuestionsFinished}
                    onSaveQuestion={saveProfileQuestion}
                    title="Matching profile questions"
                    description="Answer one structured matching question at a time. Each step saves directly to this job."
                    emptyLabel="No job profile question bank is available from the backend."
                  />
                </div>
              </div>
            )}

            {activeTab === 'advanced' && (
              <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                <Panel title="Matching Criteria Weights" icon={PiGearLight}>
                  {jobRequirements.length ? (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {jobRequirements.map(requirement => (
                        <div key={requirement.id} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                          <div className="text-sm font-medium text-[#0b1f3a]">{requirement.tagName}</div>
                          <div className="mt-1 text-xs text-slate-500">{labelize(requirement.requirementType)} · weight {requirement.weightScore || 'n/a'}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-slate-500">The matching engine has not extracted specific criteria yet.</div>
                  )}
                </Panel>

                <div className="space-y-6">
                  <Panel title="Data Completeness" icon={PiClipboardTextLight}>
                    <div className="space-y-3">
                      {profileSetup?.dataCoverage ? Object.entries(profileSetup.dataCoverage.datapoints || {}).map(([key, present]) => (
                        <div key={key} className="flex items-center justify-between gap-3 text-sm">
                          <span className="text-slate-600">{labelize(key)}</span>
                          <span className={`rounded-md px-2 py-1 text-xs font-medium ${present ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-50 text-slate-500'}`}>
                            {present ? 'Present' : 'Missing'}
                          </span>
                        </div>
                      )) : <div className="text-sm text-slate-500">No coverage data available.</div>}
                    </div>
                  </Panel>

                  {aiParsedData && (
                    <Panel title="AI Extraction Summary" icon={PiMagnifyingGlassLight}>
                      <div className="text-sm leading-6 text-slate-600">
                        Confidence score: {formatParserConfidence(aiParsedData.analysisConfidence)}.
                        {aiParsedData.skills?.length ? ` ${aiParsedData.skills.length} core skills mapped.` : ''}
                      </div>
                    </Panel>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default JobDetailsPage;
